<template>
  <div class="event-container d-flex flex-column align-items-center" :style="eventBackgroundStyle">
    <div class="content">
      <!-- ── BACK + RULES BUTTONS ─────────────────────────────────────────────── -->
      <div class="top-bar d-flex justify-content-between mb-2">
        <b-button @click="goBack" class="btn-outline-main btn-back">
          {{ $t("common.BACK") }}
        </b-button>
        <b-button @click="showModalEventRules = true" class="btn-main btn-rules">
          {{ $t("EVENT.RULES") }}
        </b-button>
      </div>

      <!-- ── EVENT “DETAIL” BANNER IMAGE ───────────────────────────────────────── -->
      <div class="detail-banner-wrapper position-relative mb-2" ref="detailBannerRef">
        <img
          v-if="event.image_detail"
          :src="getImageUrl(event.image_detail)"
          alt="Event Detail Banner"
          class="detail-banner-image w-100"
        />
      </div>

      <!-- ── TOP 3 PODIUM ──────────────────────────────────────────────────────── -->
      <div class="mb-0">
        <EventTopRanking
          :rank1User="firstPlace"
          :rank2User="secondPlace"
          :rank3User="thirdPlace"
          :isWideContainer="isWideDetail"
          :parentWidth="detailWidth"
          :fillParent="true"
        />
      </div>

      <!-- ── “ADD X TOKENS TO REACH NEXT RANK” BANNER ───────────────────────────── -->
      <div class="token-gap-banner align-content-center" v-if="isEnded">
        <p class="mb-0">{{ $t("EVENT.EVENT_ENDED") }}</p>
      </div>
      <div class="token-gap-banner align-content-center" v-else-if="isUpcoming">
        <p class="mb-0">{{ $t("EVENT.STARTING_SOON") }}</p>
      </div>
      <div v-else-if="yourPosition?.rank === 1" class="token-gap-banner align-content-center">
        <p class="mb-0" style="white-space: pre-wrap;">
          {{ $t("EVENT.MAINTAIN_YOUR_LEAD") }}
        </p>
      </div>
      <div v-else-if="yourPosition?.token_gap > 0" class="token-gap-banner align-content-center">
        <p class="mb-0">
          {{
            $t("EVENT.ADD_TOKEN_TO_NEXT_RANK", {
              token: yourPosition.token_gap,
              token_label: yourPosition.token_gap > 1 ? this.$t('common.TOKENS').toLowerCase() : this.$t('common.TOKEN').toLowerCase()
            })
          }}
        </p>
      </div>
      <div v-else class="token-gap-banner align-content-center">
        <p class="mb-0">
          {{ $t("EVENT.JOIN_LEADERBOARD") }}
        </p>
      </div>

      <!-- ── LEADERBOARD TABLE ──────────────────────────────────────────────── -->
      <div class="leaderboard-container">
        <table class="table">
          <thead>
          <tr>
            <th class="text-center font-bold" style="width: 12%; font-size: 0.92rem;">{{ $t('EVENT.RANKING') }}</th>
            <th class="text-left font-bold" style="width: 65%; padding-left: 4.3rem;
                         font-size: 0.92rem;white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              {{ $t('EVENT.PARTICIPANT') }}
            </th>
            <th class="text-center font-bold" style="width: 24%; font-size: 0.92rem;">
              {{ $t('common.TOKENS') }}
              <img src="@/assets/img/icons/arrow_up_green.svg" style="width: 11px; height: 10px; margin-left: 1px; margin-bottom: 2px" alt=""/>
            </th>
          </tr>
          </thead>
          <tbody>
          <!-- Note: tbody is now display:block with max-height, so the rows scroll -->
          <tr v-for="(user, index) in displayRows" :key="index">
            <!-- If “user” is not null, show actual data; otherwise show placeholders -->
            <template v-if="user">
              <!-- RANK COLUMN -->
              <td class="align-middle rank-cell">
                <span v-if="user.rank === 1" style="font-size: 1.8rem">🥇</span>
                <span v-else-if="user.rank === 2" style="font-size: 1.8rem">🥈</span>
                <span v-else-if="user.rank === 3" style="font-size: 1.8rem">🥉</span>
                <span v-else>{{ user.rank }}</span>
              </td>
              <!-- USERNAME + AVATAR COLUMN -->
              <td class="d-flex align-items-center user-cell">
                <img v-if="user.avatar_url" :src="getImageUrl(user.avatar_url)" class="small-avatar" alt=""/>
                <img v-else src="@/assets/img/logo_white.png" class="small-avatar p-2" alt=""/>
                <div class="d-flex flex-column align-items-start text-start">
                  <span class="table-username text-truncate">{{ user.username }}</span>
                  <span class="table-user-id text-truncate">ID: {{ user.uuid }}</span>
                </div>
              </td>
              <!-- TOKENS COLUMN -->
              <td class="align-middle tokens-cell">{{ user.token_gap }}</td>
            </template>

            <template v-else>
              <!-- RANK COLUMN: medal for 0/1/2, otherwise index+1 -->
              <td class="align-middle rank-cell">
                <span v-if="index === 0" style="font-size: 1.8rem">🥇</span>
                <span v-else-if="index === 1" style="font-size: 1.8rem">🥈</span>
                <span v-else-if="index === 2" style="font-size: 1.8rem">🥉</span>
                <span v-else>{{ index + 1 }}</span>
              </td>
              <!-- AVATAR + “—” TEXT -->
              <td class="d-flex align-items-center user-cell">
                <img src="@/assets/img/logo_white.png" class="small-avatar p-2" alt=""/>
                <div class="d-flex flex-column align-items-start text-start">
                  <span class="table-username placeholder-text">Username</span>
                  <span class="table-user-id placeholder-text">ID: xxxxxxxxxx</span>
                </div>
              </td>
              <!-- TOKENS COLUMN: dash -->
              <td class="align-middle tokens-cell">-</td>
            </template>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- ── “MY POSITION” BAR (avatar + username/ID grouped, rank on left) ───────────────────── -->
      <div v-if="yourPosition && yourPosition.rank" class="my-position-bar d-flex align-items-center justify-content-between">
        <div class="rank-left text-white">
          <span class="rank-number">{{ yourPosition?.rank ?? 'N/A' }}</span>
        </div>
        <div class="user-info d-flex align-items-center">
          <div class="avatar-wrapper">
            <img :src="yourPosition?.avatar_url ? getImageUrl(yourPosition?.avatar_url) : avatarUrl" class="avatar" alt=""/>
          </div>
          <div class="info-text text-left">
            <div class="username">{{ yourPosition?.username ?? userProfile?.name ?? "" }}</div>
            <div class="user-id">ID: {{ yourPosition?.uuid ?? userProfile?.uuid ?? "" }}</div>
          </div>
        </div>
        <div class="tokens-right d-flex align-items-center">
          <span class="token-count">{{ yourPosition?.token_gap ?? '0' }}</span>
          <img src="@/assets/img/icons/star.svg" alt="★" class="star-icon ms-2"/>
        </div>
      </div>
      <div v-else-if="isEnded" class="my-position-bar d-flex align-items-center justify-content-center click-able" @click="goToEvents">
        <p class="font-20 pt-2 pb-2">👉 {{ $t("EVENT.READ_FOR_NEXT_CHALLENGE") }}</p>
      </div>
      <div v-else-if="isUpcoming" class="my-position-bar d-flex align-items-center justify-content-center click-able">
        <p class="font-20 pt-2 pb-2">👉 {{ $t("EVENT.STAY_TUNE") }}</p>
      </div>
      <div v-else class="my-position-bar d-flex align-items-center justify-content-center click-able" @click="goToRegister">
        <p class="font-20 pt-2 pb-2">👉 {{ $t("LANDING.BUY_PROPERTY_NOW") }} !</p>
      </div>

      <ModalEventRules :event="this.event" :show="showModalEventRules" @on-close="showModalEventRules = false"/>
    </div>
  </div>
</template>

<script>
import EventTopRanking from '@/components/Events/EventTopRanking.vue';
import eventsService from '@/services/events.service';
import {urlImage} from '@/helpers/common';
import ModalEventRules from "@/components/Events/ModalEventRules.vue";
import {gtmTrackEvent} from "@/helpers/gtm";
import {GTM_EVENT_NAMES} from "@/constants/gtm";
import {EVENT_STATUS} from "@/constants/constants";

export default {
  name: 'EventDetail',
  components: {
    ModalEventRules,
    EventTopRanking
  },
  data () {
    return {
      eventUuid: this.$route.params.uuid,
      event: { image_detail: null },
      ranks: [],
      currentUserUuid: this.$store.getters.userProfile?.uuid || this.$route.query.user_uuid || null,
      // For measuring detail banner
      detailWidth: 0,
      isWideDetail: false,
      detailBreakpoint: 800,
      showModalEventRules: false,
    };
  },
  computed: {
    eventBackgroundStyle () {
      if (this.event && this.event.bg_image_detail_web) {
        return {
          backgroundImage: `url('${this.getImageUrl(this.event.bg_image_detail_web)}')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
        }
      } else if (this.$store.getters.configs && this.$store.getters.configs.event_detail_background_url_web) {
        return {
          backgroundImage: `url('${this.$store.getters.configs.event_detail_background_url_web}')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
        }
      }
      return {}
    },
    userProfile () {
      return this.$store.getters.userProfile;
    },
    avatarUrl () {
      const url = this.userProfile && this.userProfile.avatar_url || ""
      return urlImage({ image: url })
    },
    // Pull out the top 3 by rank === 1,2,3
    firstPlace () {
      return this.ranks.find(r => r.rank === 1) || null;
    },
    secondPlace () {
      return this.ranks.find(r => r.rank === 2) || null;
    },
    thirdPlace () {
      return this.ranks.find(r => r.rank === 3) || null;
    },
    displayRows () {
      // 1. Filter out anything that is null, or rank === null
      const valid = this.ranks.filter(item => item !== null && item.rank != null);
      // 2. If we still have more than 10 valid entries, just return them all (or slice to 10)
      if (valid.length >= 10) {
        return valid;
      }
      // 3. Otherwise, pad with `null` so that the returned array is exactly length === 10
      const result = [];
      for (let i = 0; i < 10; i++) {
        result.push(valid[i] || null);
      }
      return result;
    },
    // “Your” position so we know token_gap & rank
    yourPosition () {
      return this.ranks.find(r => r.uuid === this.currentUserUuid) || null;
    },

    isUpcoming () {
      return this.event && this.event.status === EVENT_STATUS.UPCOMING
    },
    isEnded () {
      return this.event && this.event.status === EVENT_STATUS.ENDED
    },

  },
  async mounted () {
    // 1) Fetch initial data
    await this.getEventDetail(true);

    // 2) Poll every 10 seconds without loading spinner
    this._pollInterval = setInterval(() => {
      this.getEventDetail(false);
    }, 10_000);

    // 3) Wait for detail banner <div> to render, then measure
    this.$nextTick(() => {
      const el = this.$refs.detailBannerRef;
      if (!el) return;

      // Initial measurement
      this.detailWidth = el.offsetWidth;
      this.isWideDetail = this.detailWidth >= this.detailBreakpoint;

      // Watch for future size changes
      this._resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          const newWidth = entry.contentRect.width;
          window.requestAnimationFrame(() => {
            if (newWidth !== this.detailWidth) {
              this.detailWidth = entry.contentRect.width;
              this.isWideDetail = this.detailWidth >= this.detailBreakpoint;
            }
          });
        }
      });
      this._resizeObserver.observe(el);
    });
  },
  beforeDestroy () {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
  },
  methods: {
    async getEventDetail (showLoading = true) {
      try {
        const res = await eventsService.getEventDetail(this.eventUuid, showLoading);
        if (res && res.event) {
          this.event = res.event;
          this.ranks = res.data || [];
        }
      } catch (err) {
        console.error('Error fetching event detail:', err);
      }
    },
    getImageUrl (path) {
      return urlImage({ image: path });
    },
    goBack () {
      this.$router.back();
    },
    async goToEvents () {
      await this.$router.push({ name: "events" })
    },
    async goToRegister () {
      gtmTrackEvent({ event: GTM_EVENT_NAMES.GET_STARTED })
      await this.$router.push({ name: "register" })
    },
  }
};
</script>

<style lang="scss" scoped>

.event-container {
  position: relative;
  min-height: 100vh;
  margin-top: -65px;
  background-color: var(--primary-hover-color);

  .content {
    margin: 0 auto;
    overflow: hidden;
    text-align: center;

    // (450 / 400 = 112.5% → clamp to 98%)
    @media screen and (min-width: 400px) {
      width: 98%;
    }

    // 450 / 550 ≈ 81.82% → round to 82%
    @media screen and (min-width: 550px) {
      width: 82%;
    }

    // 450 / 700 ≈ 64.29% → round to 64%
    @media screen and (min-width: 700px) {
      width: 64%;
    }

    // 450 / 950 ≈ 47.37% → round to 47%
    @media screen and (min-width: 950px) {
      width: 47%;
    }

    // 450 / 1100 ≈ 40.91% → round to 41%
    @media screen and (min-width: 1100px) {
      width: 41%;
    }

    // 450 / 1250 = 36.00%
    @media screen and (min-width: 1250px) {
      width: 36%;
    }

    // 450 / 1400 ≈ 32.14% → round to 32%
    @media screen and (min-width: 1400px) {
      width: 32%;
    }

    // 450 / 1550 ≈ 29.03% → round to 29%
    @media screen and (min-width: 1550px) {
      width: 29%;
    }

    // 450 / 1700 ≈ 26.47% → round to 26.5%
    @media screen and (min-width: 1700px) {
      width: 26.5%;
    }
  }
}

.event-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.event-container > .content {
  position: relative;
  z-index: 1;
}

/* ─────────────────────────────────────────────────
   ADDITIONAL STYLES FOR THE INNER CONTENT
   (only inside the .content area—your original CSS is untouched)
   ───────────────────────────────────────────────── */

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .btn-back,
  .btn-rules {
    font-family: "Figtree-Bold", Helvetica, sans-serif, serif !important;
    font-weight: bold !important;
    padding: 0.5rem 1.2rem;
    border-radius: 0.5rem;
    cursor: pointer;
  }
}

.detail-banner-wrapper {
  position: relative;
  width: 96%;
  overflow: hidden;
  border-radius: 1rem;
  margin-left: auto;
  margin-right: auto;
}

.detail-banner-image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 1rem;
}

.token-gap-banner {
  margin: 0 auto;
  min-height: 65px;
  background: linear-gradient(236.16deg, #31E3E3 -52.92%, #148989 122.21%);
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.25);
  border-radius: 0.8rem;
  padding: 0.5rem 1.8rem;
  color: white;
  font-size: 1rem;
  font-weight: 600;
}

/* ────────────────────────────────────────────────────────────────
   LEADERBOARD CONTAINER & TABLE (always 10 visible rows)
   ──────────────────────────────────────────────────────────────── */
.leaderboard-container {
  margin-left: 1rem;
  margin-right: 1rem;
  padding-left: 0.3rem;
  padding-right: 0.3rem;
  background-color: white;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

  /* Force the <table> header to stay put, and make <tbody> scroll */
  .table {
    width: 100%;
    border-collapse: collapse;
  }

  /* Freeze thead, allow tbody to scroll */
  .table thead {
    display: table;
    width: 100%;
    table-layout: fixed; /* ensures columns line up */
    background-color: white;
    z-index: 2;
  }

  .table tbody {
    display: block;
    max-height: 10 * 56px; /* 10 rows x ~56px per row = 560px */
    /* adjust 56px if your row height differs */
    overflow-y: auto;
    width: 100%;
  }

  /* Make each row behave like a table-row (so columns line up) */
  .table tbody tr {
    display: table;
    table-layout: fixed;
    width: 100%;
  }

  /* Remove default Bootstrap borders */
  .table th,
  .table td {
    border: none;
    vertical-align: middle;
    padding: 0.75rem 0.5rem;
  }

  /* ── THEAD STYLING ── */
  .table thead th {
    color: #0F6A64; /* dark teal */
    font-weight: 700;
    font-size: 0.95rem;
    text-align: left;
    padding-bottom: 0.5rem;
    padding-top: 0.5rem;
  }

  /* ── TBODY ROWS ── */
  .table tbody tr {
    background-color: white;
    transition: background-color 0.2s ease;
  }

  .table tbody tr:hover {
    background-color: #f5f5f5;
  }

  /* ── RANK CELL (LEFT COLUMN) ── */
  .rank-cell {
    width: 12%;
    text-align: center;
    color: var(--primary-color);
    font-weight: 600;
    font-size: 0.85rem;
  }

  /* Keep emojis vertically aligned */
  .rank-cell span {
    line-height: 1;
  }

  /* ── USER CELL (MIDDLE) ── */
  .user-cell {
    width: 65%;
  }

  .small-avatar {
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 50%;
    background-color: var(--primary-color);
    margin-left: 0.8rem;
    margin-right: 0.6rem;
  }

  .table-username {
    color: var(--primary-color);
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
  }

  .table-user-id {
    color: var(--primary-color);
    font-size: 0.8rem;
    font-weight: 400;
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
  }

  .user-cell > .d-flex {
    display: flex; /* already set by “d-flex” */
    align-items: center; /* vertically centered */
    column-gap: 0.25rem; /* 4px between avatar & text */
  }

  /* ── TOKENS CELL (RIGHT COLUMN) ── */
  .tokens-cell {
    width: 24%;
    text-align: center;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--primary-color);
  }

  /* ── PLACEHOLDER TEXT & AVATAR ── */
  .placeholder-text {
    color: rgba(0, 0, 0, 0.2);
    font-style: italic;
    line-height: 1.2;
  }
}

.my-position-bar {
  background: #038D95;
  border-radius: 0.8rem;
  padding: 0.8rem 1.8rem;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
}

/* Leftmost: rank number */
.my-position-bar .rank-left {
  flex: 0 0 auto; /* do not shrink */
}

.my-position-bar .rank-number {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

/* Middle-left: avatar + username/ID */
.my-position-bar .user-info {
  display: flex;
  align-items: center;
  flex: 1 1 auto; /* allow to grow slightly if needed */
}

/* Avatar wrapper fixed size */
.my-position-bar .avatar-wrapper {
  width: 3.6rem;
  height: 3.6rem;
  position: relative;
  flex: 0 0 auto;
  margin-left: 20px;
  margin-right: 8px;
}

/* Actual avatar image */
.my-position-bar .avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background-color: #E7F6F6;
  border: 2px solid #ececec;
}

/* Username / ID text (stacked vertically) */
.my-position-bar .info-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

.my-position-bar .info-text .username {
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.my-position-bar .info-text .user-id {
  font-size: 0.8rem;
  font-weight: 400;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Right: token count + star */
.my-position-bar .tokens-right {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.my-position-bar .tokens-right .token-count {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1;
}

.my-position-bar .star-icon {
  width: 1.2rem;
  height: 1.2rem;
  object-fit: contain;
  margin-left: 0.25rem; /* same as .ms-1 */
}


/* Utility to vertically space things if needed */
.mb-4 {
  margin-bottom: 1.5rem !important;
}

.mb-5 {
  margin-bottom: 3rem !important;
}
</style>
